import { createLink } from '@tanstack/react-router'
import {
  ChevronDown,
  ChevronUp,
  CirclePlus,
  Pencil,
  Settings,
  Trash2Icon,
} from 'lucide-react'
import React from 'react'
import styled, { css } from 'styled-components'

const ColorLayer = styled.div<{
  $primary?: boolean
  $danger?: boolean
  $warning?: boolean
}>`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 0;
  opacity: 0.5;
  background-color: ${({ theme, $primary, $danger, $warning }) =>
    $danger
      ? theme.forecast.danger
      : $primary
        ? theme.forecast.primary
        : $warning
          ? theme.forecast.warning
          : 'transparent'};
`

export const ChildrenContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  gap: 0.5rem;
  font-weight: 600;
`

const baseCantileverButtonStyles = css<{
  $disabled: boolean
  $ghost: boolean
  $padding?: string
  $iconOnly?: boolean
  $square?: boolean
  $textColor?: string
  $size?: 'default' | 'small'
}>`
  min-height: ${({ $size }) => ($size === 'small' ? '1.5rem' : '2rem')};
  width: ${({ $iconOnly, $square, $size }) =>
    $iconOnly || $square ? ($size === 'small' ? '1.5rem' : '2rem') : 'inherit'};
  background-color: ${({ theme, $ghost }) =>
    $ghost ? theme.forecast.defaultGhostButton : theme.forecast.defaultButton};
  color: ${({ theme, $textColor }) => $textColor || theme.forecast.defaultText};
  border: none;
  border-radius: 0.25rem;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1.0)};
  position: relative;
  overflow: hidden;
  padding: ${({ $iconOnly, $padding }) =>
    $iconOnly ? 0 : ($padding ?? '0 0.5rem')};
  aspect-ratio: ${({ $iconOnly, $square }) =>
    $iconOnly || $square ? 1 : 'inherit'};

  ${({ $disabled }) =>
    !$disabled &&
    css<{ $ghost: boolean; $textColor?: string }>`
      &:hover {
        background-color: ${({ theme, $ghost }) =>
          $ghost
            ? theme.forecast.hoverGhostButton
            : theme.forecast.hoverButton};

        ${ColorLayer} {
          opacity: 0.6;
        }
      }

      &:active {
        background-color: ${({ theme, $ghost }) =>
          $ghost
            ? theme.forecast.activeGhostButton
            : theme.forecast.activeButton};
        color: ${({ theme, $textColor }) =>
          $textColor || theme.forecast.activeText};

        ${ColorLayer} {
          opacity: 0.7;
        }
      }
    `}
`

const StyledButton = styled.button<{
  $size: 'default' | 'small'
  $disabled: boolean
  $ghost: boolean
  $padding?: string
  $iconOnly?: boolean
  $square?: boolean
  $textColor?: string
}>`
  ${baseCantileverButtonStyles}
`

const StyledLink = styled.a<{
  $disabled: boolean
  $ghost: boolean
  $padding?: string
  $iconOnly?: boolean
  $square?: boolean
  $textColor?: string
  $size?: 'default' | 'small'
}>`
  ${baseCantileverButtonStyles}
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const CantileverButton: React.FC<
  {
    children: React.ReactNode
    onClick?: React.MouseEventHandler<HTMLButtonElement>
    disabled?: boolean
    ghost?: boolean
    primary?: boolean
    danger?: boolean
    padding?: string
    warning?: boolean
    iconOnly?: boolean
    square?: boolean
    title?: string
    textColor?: string
    size?: 'default' | 'small'
  } & React.HTMLAttributes<HTMLButtonElement>
> = ({
  children,
  onClick,
  disabled = false,
  ghost = false,
  primary,
  danger = false,
  warning = false,
  padding,
  iconOnly = false,
  square = false,
  title,
  textColor,
  size = 'default',
  ...rest
}) => {
  return (
    <StyledButton
      type='button'
      $size={size}
      $ghost={ghost}
      $padding={padding}
      $disabled={disabled}
      $iconOnly={iconOnly}
      $square={square}
      $textColor={textColor}
      onClick={onClick}
      title={title}
      disabled={disabled}
      {...rest}>
      <ColorLayer $primary={primary} $danger={danger} $warning={warning} />
      <ChildrenContainer>{children}</ChildrenContainer>
    </StyledButton>
  )
}

export default CantileverButton

export const AddButton: React.FC<{
  entityName: string
  customPrefix?: string
  onClick: React.MouseEventHandler<HTMLButtonElement>
  ghost?: boolean
  primary?: boolean
  warning?: boolean
  danger?: boolean
  testId?: string
  style?: React.CSSProperties
}> = ({
  entityName,
  customPrefix,
  onClick,
  ghost,
  primary,
  warning,
  danger,
  testId,
  style,
}) => {
  return (
    <CantileverButton
      ghost={ghost}
      primary={primary}
      warning={warning}
      danger={danger}
      onClick={onClick}
      data-testid={testId}
      style={style}>
      <CirclePlus width={'1.125rem'} />
      {(customPrefix ?? 'Add') + ' ' + entityName.replace('Add ', '')}
    </CantileverButton>
  )
}

export const EditButton = React.forwardRef<
  HTMLButtonElement,
  {
    onClick?: React.MouseEventHandler<HTMLButtonElement>
    ghost?: boolean
    warning?: boolean
    showText?: boolean
    title?: string
    disabled?: boolean
  }
>(
  (
    { onClick, ghost, warning, title, showText = false, disabled = false },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ref
  ) => {
    return (
      <CantileverButton
        iconOnly={!showText}
        ghost={ghost}
        warning={warning}
        onClick={onClick}
        title={title}
        disabled={disabled}>
        <Pencil width={'1.125rem'} data-testid='edit-button' />
        {showText && 'Edit'}
      </CantileverButton>
    )
  }
)

EditButton.displayName = 'EditButton'

export const ChevronButton = React.forwardRef<
  HTMLButtonElement,
  {
    onClick?: React.MouseEventHandler<HTMLButtonElement>
    isCollapsed: boolean
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
>(({ onClick, isCollapsed = true }, ref) => {
  return (
    <CantileverButton iconOnly onClick={onClick}>
      {isCollapsed ? (
        <ChevronUp height={20} width={20} />
      ) : (
        <ChevronDown height={20} width={20} />
      )}
    </CantileverButton>
  )
})

ChevronButton.displayName = 'ChevronButton'

export const TrashButton: React.FC<
  {
    onClick?: React.MouseEventHandler<HTMLButtonElement>
    ghost?: boolean
  } & React.HTMLAttributes<HTMLButtonElement>
> = ({ onClick, ghost, ...rest }) => {
  return (
    <CantileverButton
      ghost={ghost}
      iconOnly
      danger={true}
      onClick={onClick}
      {...rest}>
      <Trash2Icon height={20} width={20} />
    </CantileverButton>
  )
}

export const SettingsButton = React.forwardRef<
  HTMLButtonElement,
  {
    onClick?: React.MouseEventHandler<HTMLButtonElement>
    ghost?: boolean
    warning?: boolean
    showText?: boolean
    title?: string
    disabled?: boolean
  }
>(
  (
    { onClick, ghost, warning, title, showText = false, disabled = false },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ref
  ) => {
    return (
      <CantileverButton
        iconOnly={!showText}
        ghost={ghost}
        warning={warning}
        onClick={onClick}
        title={title}
        disabled={disabled}>
        <Settings width={'1.125rem'} data-testid='edit-button' />
        {showText && 'Settings'}
      </CantileverButton>
    )
  }
)

SettingsButton.displayName = 'SettingsButton'

// Create the link component with CantileverButton styling
const CantileverLinkButtonComponent = React.forwardRef<
  HTMLAnchorElement,
  {
    children: React.ReactNode
    disabled?: boolean
    ghost?: boolean
    primary?: boolean
    danger?: boolean
    padding?: string
    warning?: boolean
    iconOnly?: boolean
    square?: boolean
    title?: string
    textColor?: string
  } & React.AnchorHTMLAttributes<HTMLAnchorElement>
>(
  (
    {
      children,
      disabled = false,
      ghost = false,
      primary,
      danger = false,
      warning = false,
      padding,
      iconOnly = false,
      square = false,
      title,
      textColor,
      ...rest
    },
    ref
  ) => {
    return (
      <StyledLink
        ref={ref}
        $ghost={ghost}
        $padding={padding}
        $disabled={disabled}
        $iconOnly={iconOnly}
        $square={square}
        $textColor={textColor}
        title={title}
        {...rest}>
        <ColorLayer $primary={primary} $danger={danger} $warning={warning} />
        <ChildrenContainer>{children}</ChildrenContainer>
      </StyledLink>
    )
  }
)

CantileverLinkButtonComponent.displayName = 'CantileverLinkButton'
export const CantileverLinkButton = createLink(CantileverLinkButtonComponent)
